import { Icon } from "@atoms";
import { AccessCheck } from "@particles/index";
import { useQuery } from "@tanstack/react-query";
import { faIcon } from "@utils/faIcon";
import { roundHalfUp } from "@utils/roundHalfUp";
import { Component, Fragment } from "react";
import WrapperButton from "../buttons/WrapperButton";
import { LineItemUnitTypes, PriceBookItemTypes } from "../utils/enums";
import { formatServiceDates, getLineItemUnits, valueIsDefined } from "../utils/utils";
import Banner from "./Banner";
import { fetchPriceBookData } from "./LineItemListDisplay";

function LineItemWarningBanner({ description }) {
    const currentUser = window.CURRENT_USER;
    const {
        data: pricebookItem,
        error,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ["pricebook-retrieve-by-description", currentUser.id, description],
        queryFn: () => fetchPriceBookData(currentUser, description),
        staleTime: 60000,
    });

    if (isError) {
        console.error(error);
        return null;
    }

    if (isLoading) {
        return null;
    }

    if (!pricebookItem && description) {
        return (
            <div className="line-item-label__row">
                <div className="line-item-label__field">
                    <div className="line-item-label__field-value">
                        <Banner
                            type="info"
                            text="This line item was manually created outside your standard pricebook. Custom items may lack verified pricing, costs, and detailed specifications."
                        />
                    </div>
                </div>
            </div>
        );
    }

    return null;
}

function PriceBookPartDataDisplay(props) {
    const { lineItem } = props
    const currentUser = window.CURRENT_USER

    const {
        data: pricebookItem,
        error,
        isLoading,
        isError,
    } = useQuery({
        queryKey: ["pricebook-retrieve-by-description", currentUser.id, lineItem.description],
        queryFn: () => fetchPriceBookData(currentUser, lineItem.description),
        staleTime: 60000,
    })

    if (isError) {
        console.error(error)
    } else if (pricebookItem || lineItem.description) {
        return (
            <div className="line-item-label__row">
                <div className="line-item-label__field" aria-label="Manufacturer">
                    <div className="line-item-label__field-label" aria-hidden="true">
                        Manufacturer
                    </div>
                    <div className="line-item-label__field-value" aria-label="Manufacturer">
                        {pricebookItem?.manufacturer || "\u2014"}
                    </div>
                </div>
                <div className="line-item-label__field" aria-label="Part Number">
                    <div className="line-item-label__field-label" aria-hidden="true">
                        Manufacturer Part #
                    </div>
                    <div className="line-item-label__field-value" aria-label="Part Number">
                        {pricebookItem?.part_number || "\u2014"}
                    </div>
                </div>
            </div>
        )
    }
}

class LineItemListSelect extends Component {

    renderSummary = (item) => {
        const { showSummaryHint=false } = this.props

        if (item.summary) {
            return (
                <div className="line-item-label__row">
                    <div className="line-item-label__field">
                        <div className="line-item-label__field-label">{item.line_item_type === PriceBookItemTypes.service ? "Service" : "Detailed"} Summary</div>
                        <div className="line-item-label__field-value preserve-whitespace">{item.summary}</div>
                        {item.errors && item.errors.summary && <div className="data-panel__form__field__errors" aria-label="Field Errors">{item.errors.summary}</div>}
                    </div>
                </div>
            )
        }
        else if (showSummaryHint) {
            return (
                <div className="line-item-label__row">
                    <div className="line-item-label__field">
                        <div className="line-item-label__field-label">{item.line_item_type === PriceBookItemTypes.service ? "Service" : "Detailed"} Summary</div>
                        <div className="line-item-label__field-value preserve-whitespace">(Click to provide a summary)</div>
                        {item.errors && item.errors.summary && <div className="data-panel__form__field__errors" aria-label="Field Errors">{item.errors.summary}</div>}
                    </div>
                </div>
            )
        }
    }

    renderQuickBooksItem = (item) => {
        if (item.quickbooks_desktop_item_id) {
            const quickbooks_desktop_item = window.QUICKBOOKS_DESKTOP_ITEMS.find(quickbooks_item => item.quickbooks_desktop_item_id === quickbooks_item.value)

            return (
                <div className="line-item-label__row">
                    <div className="line-item-label__field">
                        <div className="line-item-label__field-label">QuickBooks PriceBook Item</div>
                        <div className="line-item-label__field-value preserve-whitespace">{quickbooks_desktop_item !== undefined ? quickbooks_desktop_item.label : "(Click to select an item)"}</div>
                        {item.errors && item.errors.quickbooks_desktop_item_id && <div className="data-panel__form__field__errors" aria-label="Field Errors">{item.errors.quickbooks_desktop_item_id}</div>}
                    </div>
                </div>
            )
        }
        else {
            return (
                <div className="line-item-label__row">
                    <div className="line-item-label__field">
                        <div className="line-item-label__field-label">QuickBooks PriceBook Item</div>
                        <div className="line-item-label__field-value preserve-whitespace">(Click to select an item)</div>
                        {item.errors && item.errors.quickbooks_desktop_item_id && <div className="data-panel__form__field__errors" aria-label="Field Errors">{item.errors.quickbooks_desktop_item_id}</div>}
                    </div>
                </div>
            )
        }
    }

    render = () => {
        const {
            fieldName,
            onSelect,
            parent,
            items,
            formatCurrencyValue,
            showQuickBooksItemSelect=false,
            useTaxes=false,
            showSummaryHint=false,
            isEstimateLineItem=false,
            isJobLineItem=false,
            isInvoiceLineItem=false,
            isPurchaseOrderLineItem=false,
            isBillLineItem=false,
            isCreditNoteLineItem=false,
            parentLineItems=[],
            parentLineItemFieldName="",
            parentLineItemLabel="",
            hasSubtypeChoices=false
        } = this.props

        return (
            <div id={`id_${fieldName}`} className="data-panel__list-select">
                {items.map((item, index) => {
                    const { formattedDates, formattedLabel } = formatServiceDates(item.service_start_date, item.service_end_date, "UTC")
                    const { quantityUnit, priceUnit, priceLabel } = getLineItemUnits(item)

                    return (
                        <WrapperButton key={index} type="object_link" handler={event => onSelect(item)} title={item.__str__ || "New Line Item"} ariaLabel="Update Line Item">
                            <div className="data-panel__list-select__option__label line-item-label">
                                <LineItemWarningBanner description={item.description} />
                                <div className="line-item-label__row">
                                    <div className="line-item-label__field">
                                        <div className="line-item-label__field-label">Short Description</div>
                                        <div className="line-item-label__field-value">{item.description}</div>
                                        {item.errors && item.errors.description && <div className="data-panel__form__field__errors" aria-label="Field Errors">{item.errors.description}</div>}
                                    </div>
                                </div>
                                { item.line_item_type === PriceBookItemTypes.part && <PriceBookPartDataDisplay lineItem={item} /> }
                                {
                                    hasSubtypeChoices && (
                                        <div className="line-item-label__row">
                                            <div className="line-item-label__field">
                                                <div className="line-item-label__field-label">Subtype</div>
                                                <div className="line-item-label__field-value">
                                                    {item.subtype
                                                        ? item.subtype_label
                                                        : (window.CURRENT_USER?.service_company?.require_subtypes ? (
                                                            (isInvoiceLineItem || isBillLineItem || isCreditNoteLineItem)
                                                                ? "(Click to select a subtype)"
                                                                : "\u2014"
                                                        ) : "\u2014")
                                                    }
                                                </div>
                                                {item.errors && item.errors.subtype && <div className="data-panel__form__field__errors" aria-label="Field Errors">{item.errors.subtype}</div>}
                                            </div>
                                        </div>
                                    )
                                }
                                {
                                    !isEstimateLineItem && !isCreditNoteLineItem && item.line_item_type === PriceBookItemTypes.service && (isJobLineItem ? !parent.is_job_walk : true) && (
                                        <div className="line-item-label__row">
                                            <div className="line-item-label__field">
                                                <div className="line-item-label__field-label">{formattedLabel}</div>
                                                <div className="line-item-label__field-value">{formattedDates}</div>
                                                {item.errors && item.errors.service_start_date && <div className="data-panel__form__field__errors" aria-label="Field Errors">{item.errors.service_start_date}</div>}
                                                {item.errors && item.errors.service_end_date && <div className="data-panel__form__field__errors" aria-label="Field Errors">{item.errors.service_end_date}</div>}
                                            </div>
                                        </div>
                                    )
                                }
                                {
                                    item.line_item_type === PriceBookItemTypes.part && (isJobLineItem || isInvoiceLineItem || isPurchaseOrderLineItem || isCreditNoteLineItem) && (item.inventory_location || (item.errors && item.errors.inventory_location)) && (
                                        <div className="line-item-label__row">
                                            <div className="line-item-label__field">
                                                <div className="line-item-label__field-label">Inventory Location</div>
                                                <div className="line-item-label__field-value">{item.inventory_location ? item.inventory_location_label : "(Click to select a location)"}</div>
                                                {item.errors && item.errors.inventory_location && <div className="data-panel__form__field__errors" aria-label="Field Errors">{item.errors.inventory_location}</div>}
                                            </div>
                                        </div>
                                    )
                                }
                                {
                                    parentLineItemFieldName && item[parentLineItemFieldName] && (
                                        <div className="line-item-label__row">
                                            <div className="line-item-label__field">
                                                <div className="line-item-label__field-label">{parentLineItemLabel}</div>
                                                <div className="line-item-label__field-value">
                                                    {(() => {
                                                        const parentItem = parentLineItems.find(parent => String(parent.id) === String(item[parentLineItemFieldName]))
                                                        return parentItem ? parentItem.__str__ : item[parentLineItemFieldName]
                                                    })()}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                }
                                {
                                    isCreditNoteLineItem && item.line_item_type === PriceBookItemTypes.part && item.return_to_inventory && (
                                        <div className="line-item-label__row">
                                            <div className="line-item-label__field">
                                                <div className="line-item-label__field-label">Return to Inventory?</div>
                                                <div className="line-item-label__field-value">Yes</div>
                                            </div>
                                        </div>
                                    )
                                }
                                <div className="line-item-label__row">
                                    {
                                        !(isJobLineItem && window.CURRENT_USER?.permissions.cannot_view_job_financials) && (item.line_item_type === PriceBookItemTypes.part || item.line_item_type === PriceBookItemTypes.service || item.line_item_type === PriceBookItemTypes.other) && !isPurchaseOrderLineItem && !isBillLineItem && !isCreditNoteLineItem && (
                                            <div className="line-item-label__field">
                                                <div className="line-item-label__field-label">{(item.line_item_type === PriceBookItemTypes.service && item.unit_type === LineItemUnitTypes.hourly) ? "Hourly Cost" : "Unit Cost"}</div>
                                                <div className="line-item-label__field-value">{valueIsDefined(item.cost) ? `${formatCurrencyValue(item.cost).concat((item.line_item_type === PriceBookItemTypes.service && item.unit_type === LineItemUnitTypes.hourly) ? priceUnit:'')}` : "--"}</div>
                                                {item.errors && item.errors.cost && <div className="data-panel__form__field__errors" aria-label="Field Errors">{item.errors.cost}</div>}
                                            </div>
                                        )
                                    }
                                    <div className="line-item-label__field">
                                        <div className="line-item-label__field-label">Qty.</div>
                                        <div className="line-item-label__field-value">{valueIsDefined(item.quantity) ? `${parseFloat(item.quantity)}${quantityUnit}` : "--"}</div>
                                        {item.errors && item.errors.quantity && <div className="data-panel__form__field__errors" aria-label="Field Errors">{item.errors.quantity}</div>}
                                    </div>
                                    {
                                        isPurchaseOrderLineItem
                                        ?
                                            <Fragment>
                                                <div className="line-item-label__field">
                                                    <div className="line-item-label__field-label">Expected Vendor Cost</div>
                                                    <div className="line-item-label__field-value">{valueIsDefined(item.expected_cost) ? formatCurrencyValue(item.expected_cost) : "--"}</div>
                                                    {item.errors && item.errors.expected_cost && <div className="data-panel__form__field__errors" aria-label="Field Errors">{item.errors.expected_cost}</div>}
                                                </div>
                                                <div className="line-item-label__field">
                                                    <div className="line-item-label__field-label">Amount</div>
                                                    <div className="line-item-label__field-value">{valueIsDefined(item.quantity) && valueIsDefined(item.expected_cost) ? formatCurrencyValue(roundHalfUp(parseFloat(item.quantity) * parseFloat(item.expected_cost), 2)) : "--"}</div>
                                                </div>
                                            </Fragment>
                                        :
                                            !(isJobLineItem && window.CURRENT_USER?.permissions.cannot_view_job_financials) && (
                                                <Fragment>
                                                    <div className="line-item-label__field">
                                                        <div className="line-item-label__field-label">{isBillLineItem ? "Cost" : priceLabel}</div>
                                                        <div className="line-item-label__field-value">{valueIsDefined(item.price) ? `${formatCurrencyValue(item.price)}${priceUnit}` : "--"}</div>
                                                        {item.errors && item.errors.price && <div className="data-panel__form__field__errors" aria-label="Field Errors">{item.errors.price}</div>}
                                                    </div>
                                                    <div className="line-item-label__field">
                                                        <div className="line-item-label__field-label">Amount</div>
                                                        <div className="line-item-label__field-value">{valueIsDefined(item.quantity) && valueIsDefined(item.price) ? formatCurrencyValue(roundHalfUp(parseFloat(item.quantity) * parseFloat(item.price), 2)) : "--"}</div>
                                                    </div>
                                                </Fragment>
                                            )
                                    }
                                </div>
                                {
                                    isPurchaseOrderLineItem && (
                                        <div className="line-item-label__row">
                                            <div className="line-item-label__field">
                                                <div className="line-item-label__field-label">Vendor Part #</div>
                                                <div className="line-item-label__field-value">{item.part_number}</div>
                                            </div>
                                        </div>
                                    )
                                }
                                {
                                    useTaxes && item.line_item_type !== PriceBookItemTypes.discount && !isBillLineItem && (
                                        <div className="line-item-label__row">
                                            <div className="line-item-label__field">
                                                <div className="line-item-label__field-label">Tax</div>
                                                <div className="line-item-label__field-value">{<span className="data-tag" style={{margin: "0", width: "auto"}}>{item.is_taxable ? "Taxable" : "Non-Taxable"}</span>}</div>
                                            </div>
                                        </div>
                                    )
                                }
                                {this.renderSummary(item)}
                                {showQuickBooksItemSelect && this.renderQuickBooksItem(item)}
                                {
                                    isPurchaseOrderLineItem && valueIsDefined(item.estimates) && item.estimates.length !== 0 && (
                                        <AccessCheck entitlements={["entitlement_estimates_enabled"]}>
                                            <div className="line-item-label__row">
                                                <div className="line-item-label__field">
                                                    <div className="line-item-label__field-label">Associated Estimates</div>
                                                    <div className="line-item-label__field-value">{item.estimates_label}</div>
                                                </div>
                                            </div>
                                        </AccessCheck>
                                    )
                                }
                                {
                                    isPurchaseOrderLineItem && valueIsDefined(item.jobs) && item.jobs.length !== 0 && (
                                        <AccessCheck entitlements={["entitlement_jobs_enabled"]}>
                                            <div className="line-item-label__row">
                                                <div className="line-item-label__field">
                                                    <div className="line-item-label__field-label">Associated Jobs</div>
                                                    <div className="line-item-label__field-value">{item.jobs_label}</div>
                                                </div>
                                            </div>
                                        </AccessCheck>
                                    )
                                }
                                {
                                    isPurchaseOrderLineItem && valueIsDefined(item.invoices) && item.invoices.length !== 0 && (
                                        <AccessCheck entitlements={["entitlement_invoices_enabled"]}>
                                            <div className="line-item-label__row">
                                                <div className="line-item-label__field">
                                                    <div className="line-item-label__field-label">Associated Invoices</div>
                                                    <div className="line-item-label__field-value">{item.invoices_label}</div>
                                                </div>
                                            </div>
                                        </AccessCheck>
                                    )
                                }
                                {
                                    // Show equipment attribution only when the line item is actually attributed
                                    (isEstimateLineItem || isInvoiceLineItem || isJobLineItem) &&
                                    (valueIsDefined(item.equipment_display_name) || (item.errors && item.errors.equipment)) && (
                                        <div className="line-item-label__row">
                                            <div className="line-item-label__field">
                                                <div className="line-item-label__field-label">Equipment</div>
                                                <div className="line-item-label__field-value">
                                                    {valueIsDefined(item.equipment_display_name) ? item.equipment_display_name : "(Click to select equipment)"}
                                                </div>
                                                {item.errors && item.errors.equipment && <div className="data-panel__form__field__errors" aria-label="Field Errors">{item.errors.equipment}</div>}
                                            </div>
                                        </div>
                                    )
                                }
                                {
                                    // Show technician attribution only when the line item is actually attributed
                                    (isInvoiceLineItem || isJobLineItem) &&
                                    (valueIsDefined(item.user_display_name) || (item.errors && item.errors.user)) && (
                                        <div className="line-item-label__row">
                                            <div className="line-item-label__field">
                                                <div className="line-item-label__field-label">Technician</div>
                                                <div className="line-item-label__field-value">
                                                    {valueIsDefined(item.user_display_name) ? item.user_display_name : "(Click to select a technician)"}
                                                </div>
                                                {item.errors && item.errors.user && <div className="data-panel__form__field__errors" aria-label="Field Errors">{item.errors.user}</div>}
                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                            <div className="data-panel__list-select__option__icon"><Icon containerClassName="icon-inline" icon={faIcon({ name: "angle-right", style: "regular" })} /></div>
                        </WrapperButton>
                    )
                })}
            </div>
        )
    }
}

export default LineItemListSelect;
